import { formatDate, formatTime } from '@/lib/dateFormat';

// Time formatting helpers for the inbox. WhatsApp-style conventions:
//   • Same day:      "03:45 PM"
//   • Yesterday:     "Yesterday"
//   • This week:     "Mon", "Tue"
//   • Older:         "12-May-2026"
//
// Plus a relative formatter ("2m", "1h") for the conversation list.

const MS_MIN = 60_000;
const MS_HOUR = 3_600_000;
const MS_DAY = 86_400_000;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** "3:45 PM" / "Yesterday" / "Mon" / "12/05/2026" — for ConversationItem. */
export function formatInboxTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const today = startOfDay(now);
  const that = startOfDay(d);
  const diffDays = Math.round((today.getTime() - that.getTime()) / MS_DAY);

  if (diffDays === 0) {
    return formatTime(d);
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) {
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return formatDate(d);
}

/** "now" / "2m" / "1h" / "3d" / Mon Apr 22 — short label for hovercards. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < MS_MIN) return 'now';
  if (ms < MS_HOUR) return `${Math.floor(ms / MS_MIN)}m`;
  if (ms < MS_DAY) return `${Math.floor(ms / MS_HOUR)}h`;
  if (ms < 7 * MS_DAY) return `${Math.floor(ms / MS_DAY)}d`;
  return formatDate(iso);
}

/** "3:45 PM" — for message bubbles. */
export function formatMessageTime(iso: string | null | undefined): string {
  if (!iso) return '';
  return formatTime(iso);
}

/** Day-separator label: "Today" / "Yesterday" / "12-May-2026". */
export function formatDaySeparator(iso: string): string {
  const d = new Date(iso);
  const today = startOfDay(new Date());
  const that = startOfDay(d);
  const diffDays = Math.round((today.getTime() - that.getTime()) / MS_DAY);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return formatDate(d);
}

/** YYYY-MM-DD key for grouping messages by day. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
